// Smart Sash Admin Panel - Certification Service
// Aligned with Swagger API specification (docs/swagger.json)

import { axiosClient } from '../http/axiosClient';
import {
  Certification,
  CertificationDTO,
  CertificationQuestion,
  CreateCertificationQuestionRequest,
  UpdateCertificationQuestionRequest,
} from '@/models';

const BASE_PATH = '/Certification';

/**
 * Certification Service
 *
 * API endpoints per Swagger specification:
 * - GET /api/Certification - Get all certifications
 * - GET /api/Certification/{id} - Get certification by ID
 * - POST /api/Certification - Create certification
 * - DELETE /api/Certification/{id} - Delete certification
 * - GET /api/Certification/questions - Get all questions
 */
export const certificationService = {
  /**
   * Get all certifications
   * GET /api/Certification
   *
   * @param params - Optional query parameters
   * @param params.institutionId - Filter by institution ID
   * @param params.userId - Filter by user ID
   * @returns Array of certifications
   */
  getAll: async (params?: {
    institutionId?: string;
    userId?: string;
  }): Promise<CertificationDTO[]> => {
    const response = await axiosClient.get<CertificationDTO[]>(BASE_PATH, { params });
    return response.data;
  },

  /**
   * Get certification by ID
   * GET /api/Certification/{id}
   *
   * @param id - Certification ID
   * @returns Single certification
   */
  getById: async (id: string): Promise<CertificationDTO> => {
    const response = await axiosClient.get<CertificationDTO>(`${BASE_PATH}/${id}`);
    return response.data;
  },

  /**
   * Create a new certification
   * POST /api/Certification
   *
   * @param data - Certification data
   * @returns Created certification
   */
  create: async (data: Certification): Promise<CertificationDTO> => {
    const response = await axiosClient.post<CertificationDTO>(BASE_PATH, data);
    return response.data;
  },

  /**
   * Delete a certification
   * DELETE /api/Certification/{id}
   *
   * @param id - Certification ID to delete
   */
  delete: async (id: string): Promise<void> => {
    await axiosClient.delete(`${BASE_PATH}/${id}`);
  },

  /**
   * Get all certification questions
   * GET /api/Certification/questions
   *
   * @param institutionId - Optional institution ID filter
   * @returns Array of certification questions
   */
  getQuestions: async (institutionId?: string): Promise<CertificationQuestion[]> => {
    const params = institutionId ? { institutionId } : undefined;
    const response = await axiosClient.get<CertificationQuestion[]>(`${BASE_PATH}/questions`, {
      params,
    });
    return response.data;
  },

  /**
   * Create a new certification question
   * POST /api/Certification/questions
   *
   * @param data - Question data to create
   * @returns Created certification question
   */
  createQuestion: async (
    data: CreateCertificationQuestionRequest
  ): Promise<CertificationQuestion> => {
    const response = await axiosClient.post<CertificationQuestion>(`${BASE_PATH}/questions`, data);
    return response.data;
  },

  /**
   * Update an existing certification question
   * PUT /api/Certification/questions/{id}
   *
   * @param id - Question ID to update
   * @param data - Question data to update
   * @returns Updated certification question
   */
  updateQuestion: async (
    id: string,
    data: UpdateCertificationQuestionRequest
  ): Promise<CertificationQuestion> => {
    const response = await axiosClient.put<CertificationQuestion>(
      `${BASE_PATH}/questions/${id}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a certification question
   * DELETE /api/Certification/questions/{id}
   *
   * @param id - Question ID to delete
   */
  deleteQuestion: async (id: string): Promise<void> => {
    await axiosClient.delete(`${BASE_PATH}/questions/${id}`);
  },
};

export default certificationService;
